// Package imports:
import React, { useState } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import cx from 'classnames';
// Component imports:
import Input from '../../../ui-elements/Input/Formik/Input';
import Button from '../../../ui-elements/Button/Button';
import Link from '../../../ui-elements/Link/Link';
import Checkbox from '../../../ui-elements/Input/Formik/Checkbox';
import Alert from '../../../ui-elements/Alert/Alert';
import PasswordStrengthMeter from '../../PasswordRequirements/PasswordStrengthMeter';
// Service imports:
import { formatNumber, gtmPush } from '../../../services/utils';
import { GET_KELDAN_API_URL } from '../../../services/config';
import { ErrorMessages } from '../../../services/errorMessages';
import {
    ALLOWED_PASSWORD_CHARS,
    MIN_PASSWORD_LENGTH,
} from '../../../services/passwordStrength';
// Type imports:
import { SignUpType, KeldanSubOption } from '../../../types/SignUpTypes';


interface IProps {
    signUpType?: SignUpType,
    subscriptionChoice: KeldanSubOption,
    price: number,
    activeStep: number,
    setActiveStep(n: number): void
}

interface IFormProps {
    values: IFormValues
}
interface IFormValues {
    email: string,
    emailAgain: string,
    password: string,
    passwordAgain: string,
    cardNumber?: string,
    cardExp?: string,
    cvc?: string,
    consent?: boolean
}


const SignUpForm: React.FC<IProps> = ({
    subscriptionChoice,
    price,
    activeStep,
    setActiveStep
}) => {
    const [successful, setSuccessful] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);

    async function _submitForm(val: IFormValues, actions: any) {
        setLoading(true);
        try {
            const requestQuery = buildRequestQuery(val);
            const url = `${GET_KELDAN_API_URL()}/Nyskraning`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (!response.ok) {
                setLoading(false);
                setErrorMessage(ErrorMessages.RequestFailed);
                return;
            }
            const responseBody = await response.json();
            setLoading(false);
            const { success, message } = responseBody;
            setSuccessful(success);
            if (success) {
                gtmPush('buttonClick', 'nyskra', 'success');
                setActiveStep(activeStep + 1);
            } else {
                setErrorMessage(message ?? ErrorMessages.RequestFailed);
            }
        } catch (e) {
            setLoading(false);
            setErrorMessage(ErrorMessages.NetworkError);
        }
    }

    function buildRequestQuery(val: IFormValues): URLSearchParams {
        const requestQuery = new URLSearchParams({
            userFullName: '',
            name: val.email,
            email: val.email,
            emailAgain: val.emailAgain,
            password: val.password,
            passwordAgain: val.passwordAgain,
            termsAgreed: String(val.consent ?? false),
            cardNumber: val.cardNumber ?? '',
            cardExp: val.cardExp ?? '',
            cvc: val.cvc ?? '',
            plan: (subscriptionChoice === 'premium1' || subscriptionChoice === 'premium2') ? '1' : '3'
        });
        return requestQuery;
    }
    async function _handleSubmit(val: IFormValues, actions: any) {
        if (activeStep === 0) {
            setActiveStep(activeStep + 1);
        } else {
            await _submitForm(val, actions);
        }
        window.scrollTo(0, 0);
    }
    const vat = price * 0.24;

    return (
        <div className={cx('KCL_sign-up-form', 'form--signup', {
            'error-message': !!errorMessage
        })}>
            <Formik
                initialValues={{
                    email: '',
                    emailAgain: '',
                    password: '',
                    passwordAgain: '',
                    cardNumber: '',
                    cardExp: '',
                    cvc: '',
                    consent: false
                }}
                validationSchema={Yup.object({
                    email: Yup.string()
                        .email('Ógilt netfang')
                        .required('Netfang verður að fylgja'),
                    emailAgain: Yup.string()
                        .email('Ógilt netfang')
                        .oneOf([Yup.ref('email')], 'Netföng verða að vera eins')
                        .required('Netföng verða að vera eins'),
                    password: Yup.string()
                        .required('Lykilorð verður að fylgja')
                        .matches(ALLOWED_PASSWORD_CHARS, 'Lykilorð má aðeins innihalda bókstafi, tölustafi og táknin ! # _ , .')
                        .min(MIN_PASSWORD_LENGTH, `Lykilorð þarf að vera að minnsta kosti ${MIN_PASSWORD_LENGTH} stafir`)
                        .matches(/[A-ZÁÐÉÍÓÚÝÞÆÖ]/, 'Lykilorð þarf að innihalda að minnsta kosti einn hástaf (A–Ö)')
                        .matches(/[a-záðéíóúýþæö]/, 'Lykilorð þarf að innihalda að minnsta kosti einn lágstaf (a–ö)')
                        .matches(/\d/, 'Lykilorð þarf að innihalda að minnsta kosti einn tölustaf (0–9)'),
                    passwordAgain: Yup.string()
                        .required('Þú þarft að staðfesta lykilorðið')
                        .oneOf([Yup.ref('password')], 'Lykilorðin passa ekki saman'),
                    cardNumber: subscriptionChoice === 'free'
                        ? Yup.string()
                        : Yup.string()
                            .required('Kortanúmer verður að fylgja'),
                    cardExp: subscriptionChoice === 'free'
                        ? Yup.string()
                            .matches(/(\d{2}\/\d{2})?/, 'Gildistími verður að vera á forminu MM/YY')
                        : Yup.string()
                            .required('Gildistími verður að fylgja')
                            .matches(/\d{2}\/\d{2}/, 'Gildistími verður að vera á forminu MM/YY'),
                    cvc: subscriptionChoice === 'free'
                        ? Yup.string()
                            .matches(/(\d{3})?/, 'CVC verður að vera 3 tölustafir')
                        : Yup.string()
                            .required('CVC verður að fylgja')
                            .matches(/(\d{3})/, 'CVC verður að vera 3 tölustafir'),
                    consent: Yup.bool()
                        .oneOf([true], 'Samþykktu skilmálana')
                })}
                validateOnBlur={true}
                onSubmit={_handleSubmit}
                component={(props: { values: IFormValues }) => (
                    <MyForm
                        values={props.values}
                        subscriptionChoice={subscriptionChoice}
                        activeStep={activeStep}
                        price={price}
                        vat={vat}
                        loading={loading}
                        setActiveStep={setActiveStep}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                        successful={successful}
                    />
                )}
            />
        </div>
    );
}

interface ISubFormProps extends IFormProps {
    subscriptionChoice: KeldanSubOption,
    activeStep?: number,
    price: number,
    vat: number,
    loading?: boolean,
    setActiveStep?(n: number): void,
    setFieldValue?(field: string, value: any, shouldValidate?: boolean): void,
    errorMessage?: string,
    setErrorMessage?(message: string): void,
    successful?: boolean
}

const MyForm: React.FC<ISubFormProps> = ({
    values,
    subscriptionChoice,
    activeStep,
    price,
    vat,
    loading,
    setActiveStep = () => { },
    errorMessage,
    setErrorMessage = () => { },
    successful
}) => {
    const { setFieldValue } = useFormikContext();
    const displayFormBody = () => {
        if (errorMessage) {
            return (
                <div>
                    <Alert
                        type='error'
                        headText='Villa'
                        text={<p>{errorMessage}</p>}
                    />
                    <div className="signup-error__actions">
                        <Link
                            onClick={() => {
                                setActiveStep(0);
                                setErrorMessage('');
                                values.consent = false;
                                values.cvc = '';
                            }}
                            linkSize='14'
                            icon='back'
                        >
                            Fara til baka og breyta
                        </Link>
                    </div>
                </div>
            )
        }
        switch (activeStep) {
            case 0:
                return (
                    <StepZero
                        values={values}
                        subscriptionChoice={subscriptionChoice}
                        price={price}
                        vat={vat}
                        setFieldValue={setFieldValue}
                    />
                )
            case 1:
                return (
                    <StepOne
                        values={values}
                        subscriptionChoice={subscriptionChoice}
                        price={price}
                        vat={vat}
                        loading={loading}
                        setActiveStep={setActiveStep}
                    />
                )
            case 2:
                if (successful) {
                    return (
                        <StepTwo
                            values={values}
                            subscriptionChoice={subscriptionChoice}
                            price={price}
                            vat={vat}
                        />
                    )
                }
                return null;
        }
    }
    return <Form>
        {displayFormBody()}
    </Form>
}

const StepZero: React.FC<ISubFormProps> = ({
    values,
    subscriptionChoice,
    price,
    vat,
    setFieldValue = () => { }
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const togglePassword = () => setShowPassword(!showPassword);
    return <div className="sign-up-step--0">
        <div className="form__body">
            <div className="form__section">
                <h3>Upplýsingar um notanda</h3>
                <div className="form__row">
                    <div className="form__col">
                        <Input
                            label="Netfang"
                            name="email"
                            id="email"
                            autoComplete='new-password'
                            value={values.email}
                        />
                    </div>
                    <div className="form__col">
                        <Input
                            label="Netfang aftur"
                            name="emailAgain"
                            id="emailAgain"
                            autoComplete='new-password'
                            value={values.emailAgain}
                        />
                    </div>
                </div>
                <div className="form__row">
                    <div className="form__col">
                        <Input
                            label="Lykilorð"
                            inputType='password'
                            name="password"
                            id="password"
                            autoComplete='new-password'
                            value={values.password}
                            visible={showPassword}
                            toggleVisible={togglePassword}
                        />
                    </div>
                    <div className="form__col">
                        <Input
                            label="Lykilorð aftur"
                            inputType='password'
                            name="passwordAgain"
                            id="passwordAgain"
                            autoComplete='new-password'
                            value={values.passwordAgain}
                            visible={showPassword}
                            toggleVisible={togglePassword}
                        />
                    </div>
                </div>
                <div className="form__row form__row--password-meta">
                    <div className="form__col">
                        <PasswordStrengthMeter password={values.password ?? ''} />
                    </div>
                </div>
            </div>
            <div className="form__section">
                <h3>Greiðsluupplýsingar</h3>
                {subscriptionChoice === 'free' &&
                    <p className="paragraph">
                        Til að geta keypt skýrslur inni á Keldunni þurfa greiðsluupplýsingar að vera til staðar.
                        Þú getur valið að fylla inn eftirfarandi reiti núna eða síðar inni á síðunni <span className='paragraph--bold'>Stillingar</span>.
                    </p>
                }
                <div className="form__group">
                    <h4>Kortaupplýsingar</h4>
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                label="Kortanúmer"
                                inputType="creditCardFormat"
                                name="cardNumber"
                                id="cardNumber"
                                value={values.cardNumber}
                            />
                        </div>
                        <div className="form__col">
                            <Input
                                label="MM/YY"
                                inputType="expirationFormat"
                                name="cardExp"
                                id="cardExp"
                                value={values.cardExp}
                            />
                        </div>
                        <div className="form__col form__col--cvc">
                            <Input
                                label="CVC"
                                inputType="cvcFormat"
                                name="cvc"
                                id="cvc"
                                value={values.cvc}
                            />
                            <div className="form__icon">
                                <img src="https://cdn.livemarketdata.com/KeldanImages/cvc.svg" alt="CVC" />
                            </div>
                        </div>
                    </div>
                </div>
                {subscriptionChoice !== 'free' && (
                    <div className="form__group form__group-details">
                        <dl>
                            <dt>Mánaðarleg áskrift að Keldunni fyrir fyrirtæki</dt>
                            <dd>{formatNumber(price, null, 0)} kr.</dd>
                        </dl>
                        <dl>
                            <dt>Virðisauki 24%</dt>
                            <dd>{formatNumber(vat, null, 0)} kr.</dd>
                        </dl>
                        <dl className="form__group-total">
                            <dt>Samtals til greiðslu</dt>
                            <dd>{formatNumber(price + vat, null, 0)} kr.</dd>
                        </dl>
                    </div>
                )}
            </div>
            <div className="form__section">
                <div className="form__group form__group--consent">
                    <Checkbox
                        id="consent"
                        label={<span>Ég hef lesið og skilið <Link url="/Skilmalar" linkSize='16' targetBlank>skilmála Keldunnar</Link></span>}
                        name="consent"
                        value={values.consent?.toString()}
                    />
                </div>
            </div>
        </div>
        <div className="form__actions">
            <Button
                buttonType="primary"
            >
                Halda áfram
            </Button>
        </div>
    </div>
}

const StepOne: React.FC<ISubFormProps> = ({
    values,
    subscriptionChoice,
    price,
    vat,
    loading,
    setActiveStep = () => { }
}) => {
    return <div className="sign-up-step--1">
        <p className='paragraph--bold'>
            Eru allar upplýsingar rétt fylltar út? Ef ekki getur þú farið til baka og breytt.
        </p>
        <div className="form__body confirmation-step">
            <div className="form__section">
                <h4>Upplýsingar um notanda</h4>
                <dl>
                    <dt>Netfang:</dt>
                    <dd>{values.email}</dd>
                </dl>
            </div>
            {values.cardNumber && <div className="form__section">
                <h4>Kortaupplýsingar</h4>
                <dl>
                    <dt>Kreditkort:</dt>
                    <dd>************ {values.cardNumber?.substring(12)}</dd>
                    <dd>{values.cardExp}</dd>
                    <dd>{values.cvc}</dd>
                </dl>
            </div>}
            <div>
                {subscriptionChoice === 'free'
                    ? <p className='paragraph-small--bold'>Frí aðgangur að Keldunni</p>
                    : <>
                        <p className='paragraph-small--bold'>Mánaðarleg áskrift að Keldunni fyrir fyrirtæki</p>
                        <p className='paragraph-small--bold'>Samtals til greiðslu: {formatNumber(price + vat, null, 0)} kr. með VSK</p>
                    </>
                }
            </div>
        </div>
        <div className="form__actions">
            <Link
                onClick={() => {
                    setActiveStep(0);
                    values.consent = false;
                    values.cvc = '';
                }}
                linkSize='14'
                icon='back'
            >
                Fara til baka og breyta
            </Link>
            <Button showLoader={loading} buttonType="primary" size="lg">Staðfesta</Button>
        </div>
    </div>
}

const StepTwo: React.FC<ISubFormProps> = ({
    values,
    subscriptionChoice,
    price,
    vat
}) => {
    return <div className="sign-up-step--2">
        <p><strong>Staðfestingarpóstur hefur verið sendur á {values.email}. Þú þarft að staðfesta netfangið þitt áður en þú getur skráð þig inn.</strong></p>
        <p>Pósturinn berst frá admin@keldan.is en skoðaðu ruslpóst ef tölvupósturinn hefur ekki borist í innhólf.</p>
        <div className="form__body final-step">
            <div className="form__section">
                <h4>Upplýsingar um notanda</h4>
                <dl>
                    <dt>Netfang:</dt>
                    <dd>{values.email}</dd>
                </dl>
            </div>
            {values.cardNumber && <div className="form__section">
                <h4>Kortaupplýsingar</h4>
                <dl>
                    <dt>Kreditkort:</dt>
                    <dd>************ {values.cardNumber?.substring(12)}</dd>
                    <dd>{values.cardExp}</dd>
                    <dd>{values.cvc}</dd>
                </dl>
            </div>}
            <div>
                {subscriptionChoice === 'free'
                    ? <p><strong>Frí aðgangur að Keldunni</strong></p>
                    : <>
                        <p><strong>Mánaðarleg áskrift að Keldunni fyrir fyrirtæki</strong></p>
                        <p><strong>Samtals til greiðslu: {formatNumber(price + vat, null, 0)} kr. með VSK</strong></p>
                    </>
                }
            </div>
        </div>
    </div>
}

export default SignUpForm;
